import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Check, PencilSimple, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import styles from "./InlineEdit.module.css";

type Props = {
  label: string;
  value: string;
  /** Wird bei Save aufgerufen (nur bei tatsächlicher Änderung + Pflichtfeld-Erfüllung). */
  onSave: (next: string) => void;
  placeholder?: string;
  emptyPlaceholder?: string;
  multiline?: boolean;
  maxLength?: number;
  required?: boolean;
  ariaEditLabel?: string;
};

/** Inline-editierbare Zeile: zeigt Label + Wert + Stift-Icon. Klick auf den
 * Stift aktiviert den Edit-Modus (Input oder Textarea + Save/Cancel).
 *
 * Keyboard:
 * - Enter speichert (Input-Modus, nicht bei Textarea → dort Cmd/Ctrl+Enter)
 * - Escape verwirft
 */
export function InlineEdit({
  label,
  value,
  onSave,
  placeholder,
  emptyPlaceholder,
  multiline = false,
  maxLength,
  required = false,
  ariaEditLabel,
}: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (required && !next) return;
    if (next !== value) onSave(next);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === "Enter" && multiline && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  };

  if (editing) {
    return (
      <div className={`${styles.row} ${styles.rowEditing}`}>
        <span className={styles.label}>{label}</span>
        <div className={styles.editField}>
          {multiline ? (
            <textarea
              ref={(el) => {
                inputRef.current = el;
              }}
              className={styles.textarea}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              maxLength={maxLength}
              placeholder={placeholder}
              rows={3}
            />
          ) : (
            <input
              ref={(el) => {
                inputRef.current = el;
              }}
              type="text"
              className={styles.input}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              maxLength={maxLength}
              placeholder={placeholder}
            />
          )}
          <div className={styles.editButtons}>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.cancel}`}
              onClick={cancel}
              aria-label={t("common.cancel")}
            >
              <X size={16} weight="regular" />
            </button>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.save}`}
              onClick={commit}
              aria-label={t("common.save")}
              disabled={required && !draft.trim()}
            >
              <Check size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isEmpty = !value;

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={`${styles.value} ${isEmpty ? styles.valueEmpty : ""}`}>
        {isEmpty ? emptyPlaceholder ?? "—" : value}
      </span>
      <button
        type="button"
        className={styles.editTrigger}
        onClick={() => setEditing(true)}
        aria-label={ariaEditLabel ?? `${label} bearbeiten`}
      >
        <PencilSimple size={16} weight="regular" />
      </button>
    </div>
  );
}
